// Story 064 → TRIAGE-02 (audit 3.2 / IMP-02) — global Ctrl+Z (Cmd+Z)
// undo + Ctrl+Shift+Z redo handler + the inverse/replay dispatch logic.
//
// Mounted once at the AnalysisFeature level. Reads `editAllowed` from
// the D-5 gate already computed there.
//
// UNDO (Ctrl+Z):
//   1. Pop the GLOBAL top entry (IMP-02 — the stack is no longer
//      per-image). Empty stack → no-op.
//   2. When the entry's image is not the active one, ACTIVATE it first
//      so the inspector sees what is being reverted.
//   3. Fire the inverse mutation:
//        - updateDefect          → PATCH /defects/:id with `before`.
//        - softDeleteDefect      → POST /defects/:id/restore.
//        - createDefectFromFinding → POST /findings/:id/revert-promotion.
//        - dismissFinding        → POST /ai-findings/:id/undismiss.
//          NOTE: the BE role-gates `finding.undismiss`. Christian's Q3
//          decision says SELF-undo bypasses the gate — that BE change
//          ships separately; until it lands a non-privileged user's
//          undo can 403. We surface it via `onUndoError` and put the
//          entry BACK on the undo stack (nothing was reverted).
//        - linkDefects / unlinkDefects → story-029 stubs; they throw.
//   4. Append an audit-log row with action: 'undo' + the popped
//      entry's auditLogId.
//
// REDO (Ctrl+Shift+Z):
//   Pop the redo top and REPLAY the forward mutation (PATCH `after`,
//   re-delete, re-promote, re-dismiss). On success the entry returns to
//   the undo stack via `pushRestored` (which does NOT clear redo);
//   `createDefectFromFinding` swaps in the NEW defectId from the replay
//   response. Any new forward push clears the redo stack (store-side).
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import type { DefectSeverity, DefectStatus } from "@aeriosiq/types";
import { defectKeys } from "../defects/queries.js";
import { findingKeys } from "../findings/queries.js";
import type {
  AuditLogAppendBody,
  AuditLogAppendResponse,
} from "../audit/queries.js";
import { useImageActivationStore } from "./state/imageActivationStore.js";
import { useUndoStackStore, type UndoEntry } from "./state/undoStackStore.js";

export interface UseUndoHotkeyArgs {
  /** Owning data acquisition. Always supplied — the audit row needs it. */
  dataAcquisitionId: string;
  /** D-5 gate from the parent. Falsy → handler is inert. */
  editAllowed: boolean;
  /**
   * TRIAGE-02 — surfaced when an inverse/replay mutation fails in a way
   * the inspector must know about (e.g. the undismiss role gate, see
   * header). The host renders it in its single error-snackbar slot.
   */
  onUndoError?: ((message: string) => void) | undefined;
}

/**
 * Install global `keydown` listeners for Ctrl/Cmd+Z (undo) and
 * Ctrl/Cmd+Shift+Z (redo). Returns imperative `triggerUndo` /
 * `triggerRedo` so the header buttons share the implementation.
 *
 * Concurrency: pops are synchronous (no race possible with the stack),
 * but inverse mutations are fired in PARALLEL. We use the API client
 * directly (not the wrapped useMutation hooks) so each call is a fully
 * independent fetch, then nudge the query cache by invalidating the
 * relevant keys.
 */
export function useUndoHotkey({
  dataAcquisitionId,
  editAllowed,
  onUndoError,
}: UseUndoHotkeyArgs): {
  triggerUndo: () => void;
  triggerRedo: () => void;
} {
  const api = useApi();
  const qc = useQueryClient();
  // Refs so the listener closure captures the latest handles without
  // re-binding the listener on every render.
  const refs = useRef({
    dataAcquisitionId,
    editAllowed,
    api,
    qc,
    onUndoError,
  });
  refs.current = {
    dataAcquisitionId,
    editAllowed,
    api,
    qc,
    onUndoError,
  };

  const invalidateDefects = (): void => {
    const r = refs.current;
    void r.qc.invalidateQueries({
      queryKey: [...defectKeys.all, "list", r.dataAcquisitionId],
    });
  };
  const invalidateFindings = (): void => {
    const r = refs.current;
    void r.qc.invalidateQueries({
      queryKey: [...findingKeys.all, "list", r.dataAcquisitionId],
    });
  };

  const auditFollowup = async (
    entry: UndoEntry,
    forwardArgs: Omit<AuditLogAppendBody, "action" | "referencesAuditLogId">,
  ): Promise<void> => {
    const r = refs.current;
    try {
      await r.api.post<AuditLogAppendResponse>("/audit-log", {
        ...forwardArgs,
        action: "undo",
        referencesAuditLogId: entry.auditLogId,
      });
    } catch (e: unknown) {
      console.warn("[064] undo audit-log append failed", e);
    }
  };

  /** IMP-02 — bring the entry's image into view before reverting it. */
  const activateEntryImage = (entry: UndoEntry): void => {
    const activation = useImageActivationStore.getState();
    if (activation.activeImageId !== entry.imageId) {
      activation.setActive(entry.imageId);
    }
  };

  const runInverse = async (entry: UndoEntry): Promise<void> => {
    const r = refs.current;
    switch (entry.kind) {
      case "updateDefect": {
        const body: {
          status?: DefectStatus;
          severity?: DefectSeverity;
          defectClassId?: string;
        } = {};
        if (entry.before.status !== undefined)
          body.status = entry.before.status;
        // 8.0.0 — `before.severity === null` means "was not graded". The
        // request-side enum is non-nullable, so null is OMITTED from the
        // inverse body. That is faithful against the real BE: the wire
        // severity field never writes the row (severity is derived from
        // the defect class); the refetch below restores the honest value.
        if (
          entry.before.severity !== undefined &&
          entry.before.severity !== null
        )
          body.severity = entry.before.severity;
        if (entry.before.defectClassId !== undefined)
          body.defectClassId = entry.before.defectClassId;
        if (Object.keys(body).length > 0) {
          try {
            await r.api.patch(
              `/defects/${encodeURIComponent(entry.defectId)}`,
              body,
            );
          } catch (e: unknown) {
            console.warn("[064] undo updateDefect failed", e);
            useUndoStackStore.getState().discardRedoEntry(entry);
            return;
          }
        }
        invalidateDefects();
        await auditFollowup(entry, {
          entityType: "defect",
          entityId: entry.defectId,
          dataAcquisitionId: r.dataAcquisitionId,
          before: { ...entry.after },
          after: { ...entry.before },
        });
        return;
      }
      case "softDeleteDefect": {
        try {
          await r.api.post(
            `/defects/${encodeURIComponent(entry.defect.id)}/restore`,
            { previousStatus: entry.defect.status },
          );
        } catch (e: unknown) {
          console.warn("[064] undo softDeleteDefect failed", e);
          useUndoStackStore.getState().discardRedoEntry(entry);
          return;
        }
        invalidateDefects();
        await auditFollowup(entry, {
          entityType: "defect",
          entityId: entry.defect.id,
          dataAcquisitionId: r.dataAcquisitionId,
          before: null,
          after: { ...entry.defect },
        });
        return;
      }
      case "createDefectFromFinding": {
        try {
          await r.api.post(
            `/findings/${encodeURIComponent(entry.findingId)}/revert-promotion`,
            { defectId: entry.defectId },
          );
        } catch (e: unknown) {
          console.warn("[064] undo createDefectFromFinding failed", e);
          useUndoStackStore.getState().discardRedoEntry(entry);
          return;
        }
        invalidateDefects();
        invalidateFindings();
        await auditFollowup(entry, {
          entityType: "defect",
          entityId: entry.defectId,
          dataAcquisitionId: r.dataAcquisitionId,
          before: { findingId: entry.findingId },
          after: null,
        });
        return;
      }
      case "dismissFinding": {
        try {
          await r.api.post(
            `/ai-findings/${encodeURIComponent(entry.findingId)}/undismiss`,
            {},
          );
        } catch (e: unknown) {
          console.warn("[TRIAGE-02] undo dismissFinding failed", e);
          // Nothing was reverted — the dismissal stands. Put the entry
          // BACK on the undo stack (not redo) and tell the inspector.
          // The 403 case is the known BE self-undo role-gate gap
          // (Q3 decided; BE fix ships separately).
          const store = useUndoStackStore.getState();
          store.discardRedoEntry(entry);
          store.pushRestored(entry);
          const status = (e as { response?: { status?: number } }).response
            ?.status;
          r.onUndoError?.(
            status === 403
              ? "Could not restore the dismissed finding — your role lacks undismiss permission."
              : "Could not restore the dismissed finding — please retry.",
          );
          return;
        }
        invalidateFindings();
        await auditFollowup(entry, {
          entityType: "aiFinding",
          entityId: entry.findingId,
          dataAcquisitionId: r.dataAcquisitionId,
          before: { status: "dismissed" },
          after: { status: "pending" },
        });
        return;
      }
      case "linkDefects":
      case "unlinkDefects":
        throw new Error(
          `[064] undo for ${entry.kind} not implemented (Story 029 / PR-C)`,
        );
    }
  };

  /** TRIAGE-02 — replay the FORWARD mutation of a redone entry. */
  const runReplay = async (entry: UndoEntry): Promise<void> => {
    const r = refs.current;
    const restore = (e: UndoEntry): void => {
      useUndoStackStore.getState().pushRestored(e);
    };
    switch (entry.kind) {
      case "updateDefect": {
        const body: {
          status?: DefectStatus;
          severity?: DefectSeverity;
          defectClassId?: string;
        } = {};
        if (entry.after.status !== undefined) body.status = entry.after.status;
        // 8.0.0 — same null-omission rule as the inverse PATCH above.
        if (entry.after.severity !== undefined && entry.after.severity !== null)
          body.severity = entry.after.severity;
        if (entry.after.defectClassId !== undefined)
          body.defectClassId = entry.after.defectClassId;
        if (Object.keys(body).length > 0) {
          try {
            await r.api.patch(
              `/defects/${encodeURIComponent(entry.defectId)}`,
              body,
            );
          } catch (e: unknown) {
            console.warn("[TRIAGE-02] redo updateDefect failed", e);
            return;
          }
        }
        invalidateDefects();
        restore(entry);
        return;
      }
      case "softDeleteDefect": {
        try {
          await r.api.delete(`/defects/${encodeURIComponent(entry.defect.id)}`);
        } catch (e: unknown) {
          console.warn("[TRIAGE-02] redo softDeleteDefect failed", e);
          return;
        }
        invalidateDefects();
        restore(entry);
        return;
      }
      case "createDefectFromFinding": {
        try {
          const response = await r.api.post<{ defectId: string }>(
            `/findings/${encodeURIComponent(entry.findingId)}/promote`,
            {},
          );
          // The replay mints a NEW defect — the restored undo entry must
          // carry the new id so a subsequent undo reverts the right row.
          restore({ ...entry, defectId: response.data.defectId });
        } catch (e: unknown) {
          console.warn("[TRIAGE-02] redo createDefectFromFinding failed", e);
          return;
        }
        invalidateDefects();
        invalidateFindings();
        return;
      }
      case "dismissFinding": {
        try {
          await r.api.post(
            `/findings/${encodeURIComponent(entry.findingId)}/dismiss`,
            entry.reason ? { reason: entry.reason } : {},
          );
        } catch (e: unknown) {
          console.warn("[TRIAGE-02] redo dismissFinding failed", e);
          return;
        }
        invalidateFindings();
        restore(entry);
        return;
      }
      case "linkDefects":
      case "unlinkDefects":
        console.warn(
          `[TRIAGE-02] redo for ${entry.kind} not implemented (Story 029)`,
        );
        return;
    }
  };

  const triggerUndo = (): void => {
    const r = refs.current;
    if (!r.editAllowed) return;
    const entry = useUndoStackStore.getState().popUndo();
    if (!entry) return;
    activateEntryImage(entry);
    // Fire-and-forget. The stack pop is synchronous so subsequent
    // Ctrl+Z presses see the right top regardless of whether this
    // press's inverse PATCH has resolved yet.
    void runInverse(entry);
  };

  const triggerRedo = (): void => {
    const r = refs.current;
    if (!r.editAllowed) return;
    const entry = useUndoStackStore.getState().popRedo();
    if (!entry) return;
    activateEntryImage(entry);
    void runReplay(entry);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMod = e.ctrlKey || e.metaKey;
      if (!isMod) return;
      if (e.key !== "z" && e.key !== "Z") return;
      // Skip when the focused element is a text input or contenteditable
      // — undo there should fall back to the browser's native
      // text-edit undo. The popover searchbox + the panel filter
      // input are the two real cases.
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      // TRIAGE-02 — Ctrl+Shift+Z = redo (the story-064 skip is retired).
      if (e.shiftKey) {
        triggerRedo();
      } else {
        triggerUndo();
      }
    };
    window.addEventListener("keydown", onKey);
    useUndoStackStore.getState().setTriggerUndo(triggerUndo);
    useUndoStackStore.getState().setTriggerRedo(triggerRedo);
    return () => {
      window.removeEventListener("keydown", onKey);
      useUndoStackStore.getState().setTriggerUndo(null);
      useUndoStackStore.getState().setTriggerRedo(null);
    };
    // triggerUndo/triggerRedo are captured via refs.current and stable —
    // empty dependency list is intentional to avoid re-binding on every
    // render.
  }, []);

  return { triggerUndo, triggerRedo };
}
